/**
 * Live real-time collaboration for the document editor (Loro-WASM + relay).
 *
 * Binds a TipTap editor to a {@link LoroDocument} and keeps it converged with
 * remote peers through the `/api/collab` relay, driving the same version-vector
 * sync loop proven in `collab/__tests__/convergence.test.ts` (and its Rust twin
 * `collab-rust/tests/poll_relay.rs`). The bridge between TipTap's block JSON and
 * Loro's rich-text delta is the pure `tiptap-delta` conversion.
 *
 * Design (conservative, mirrors the existing PowerSync remote-change handling in
 * `usePageEditor`): local edits are pushed by rebuilding the delta and reconciling
 * it into Loro; remote updates are pulled on a poll timer and applied to the
 * editor only when the user is not actively typing, so an incoming merge never
 * fights the local cursor. Loro guarantees convergence regardless of order.
 *
 * The module is loaded lazily (dynamic import in {@link startCollaboration}) so
 * the ~1 MB Loro WASM is only fetched when a user actually starts a session.
 */
import { createSignal, onCleanup } from "solid-js";
import type { Editor } from "@tiptap/core";
import { SUGGESTION_SKIP_META } from "@/features/comments/lib/suggestion-mode";

export type CollabStatus = "idle" | "connecting" | "connected" | "error";

const POLL_INTERVAL_MS = 700;

export interface CollaborationSession {
  status: () => CollabStatus;
  /** The room code peers use to join (shown in the UI). */
  code: () => string | null;
  stop: () => void;
}

export function useDocumentCollaboration(editor: () => Editor | null) {
  const [status, setStatus] = createSignal<CollabStatus>("idle");
  const [code, setCode] = createSignal<string | null>(null);

  let timer: ReturnType<typeof setInterval> | null = null;
  let stopped = false;

  const stop = () => {
    stopped = true;
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
    setStatus("idle");
    setCode(null);
  };

  onCleanup(stop);

  /** Start (create) or join a collaboration room. Returns the room code. */
  const start = async (joinCode?: string): Promise<string | null> => {
    const instance = editor();
    if (!instance) return null;
    setStatus("connecting");
    stopped = false;

    try {
      // Lazy-load Loro-WASM + the relay client only when collaboration begins.
      const [{ LoroDocument }, relayMod, bridge] = await Promise.all([
        import("../collab/loro-document"),
        import("../collab/relay-client"),
        import("../lib/tiptap-delta"),
      ]);
      const { RelayClient, newSyncState, syncRound } = relayMod;

      const relay = new RelayClient({
        fetch: (path, init) => fetch(path, { ...init, credentials: "include" }),
      });
      const room = joinCode ? await relay.joinRoom(joinCode) : await relay.createRoom();
      setCode(room.code);

      // Seed the CRDT from the editor's current content.
      const peerId = BigInt(Math.floor(Math.random() * 0xffffffff)) + 1n;
      const doc = LoroDocument.fromDelta(peerId, bridge.tiptapToDelta(instance.getJSON()));
      const state = newSyncState();

      // Push local edits into Loro as they happen. `reconcile` applies the
      // MINIMAL set of ops (Myers-diff text + per-range mark changes), preserving
      // the CRDT identity of unchanged characters so a peer editing elsewhere
      // merges cleanly instead of duplicating the body.
      const onUpdate = () => {
        doc.reconcile(bridge.tiptapToDelta(instance.getJSON()));
      };
      instance.on("update", onUpdate);
      onCleanup(() => instance.off("update", onUpdate));

      setStatus("connected");

      // Poll loop: sync, then reflect remote changes into the editor when idle.
      let lastFingerprint = doc.fingerprint();
      timer = setInterval(() => {
        void (async () => {
          if (stopped) return;
          try {
            await syncRound(doc, relay, room, state);
            const fp = doc.fingerprint();
            if (fp !== lastFingerprint) {
              lastFingerprint = fp;
              if (!instance.isFocused) {
                // Skip-meta: a remote CRDT refresh must never be re-tracked
                // as a local suggestion by suggestion mode.
                instance
                  .chain()
                  .setMeta(SUGGESTION_SKIP_META, true)
                  .setContent(bridge.deltaToTiptap(doc.toDelta()))
                  .run();
              }
            }
          } catch (err) {
            console.error("[documents] collaboration sync failed:", err);
          }
        })();
      }, POLL_INTERVAL_MS);

      return room.code;
    } catch (err) {
      console.error("[documents] failed to start collaboration:", err);
      setStatus("error");
      return null;
    }
  };

  const session: CollaborationSession = { status, code, stop };
  return { session, start, stop };
}
